import { getEffectiveFieldAuditLevel, } from '@rammetec/data-access';
import { AUDIT_FIELD_NAMES } from '../../descriptors/_auditFields.js';
import { getDescriptorForRule } from '../../descriptors/descriptorLookup.js';
export const AUDIT_DEFAULT_RETENTION_DAYS = 180;
/** Fields never recorded in the field-audit log. */
export const TECHNICAL_FIELDS = new Set([
    'id',
    'organizationId',
    'version',
    'active',
    ...AUDIT_FIELD_NAMES,
]);
function isEmptyValue(v) {
    return v === null || v === undefined || v === '';
}
function toDateMs(v) {
    if (v instanceof Date) {
        const t = v.getTime();
        return Number.isFinite(t) ? t : null;
    }
    if (typeof v === 'string' && v.trim() !== '') {
        const t = Date.parse(v);
        return Number.isFinite(t) ? t : null;
    }
    return null;
}
/** Stable structural fingerprint — key order does not matter. */
function stableFingerprint(value) {
    if (value === null || typeof value !== 'object') {
        return JSON.stringify(value);
    }
    if (value instanceof Date) {
        return JSON.stringify(value.toISOString());
    }
    if (Array.isArray(value)) {
        return `[${value.map(stableFingerprint).join(',')}]`;
    }
    const obj = value;
    const keys = Object.keys(obj).sort();
    return `{${keys.map((k) => `${JSON.stringify(k)}:${stableFingerprint(obj[k])}`).join(',')}}`;
}
/**
 * Equality used by the field-audit diff.
 * Empty (`null` / `undefined` / `''`) are equal; dates compare by instant;
 * numbers coerce with `Number`; objects/arrays compare structurally.
 */
export function valuesEqual(a, b, field) {
    if (isEmptyValue(a) && isEmptyValue(b))
        return true;
    if (field?.type === 'date' || a instanceof Date || b instanceof Date) {
        const ta = toDateMs(a);
        const tb = toDateMs(b);
        if (ta != null && tb != null)
            return ta === tb;
    }
    if (field?.type === 'number') {
        if (isEmptyValue(a) || isEmptyValue(b))
            return isEmptyValue(a) && isEmptyValue(b);
        return Number(a) === Number(b);
    }
    if ((a !== null && typeof a === 'object') ||
        (b !== null && typeof b === 'object')) {
        if (isEmptyValue(a) || isEmptyValue(b))
            return false;
        return stableFingerprint(a) === stableFingerprint(b);
    }
    return a === b;
}
/**
 * Diff `payload` keys against `before` for field-audit recording.
 *
 * - Descriptor via {@link getDescriptorForRule}(`entity`, `'field-audit'`).
 * - Missing descriptor ⇒ every non-technical key at `'names-only'` (closed failure).
 * - Create (`before === null`): only non-empty values.
 */
export function diffForAudit(before, payload, entity) {
    const descriptor = getDescriptorForRule(entity, 'field-audit');
    const fieldByName = descriptor
        ? new Map(descriptor.fields.map((f) => [f.name, f]))
        : null;
    const beforeRec = (before ?? {});
    const payloadRec = payload;
    const changes = [];
    for (const key of Object.keys(payloadRec)) {
        if (TECHNICAL_FIELDS.has(key))
            continue;
        const field = fieldByName?.get(key);
        if (fieldByName) {
            if (!field)
                continue;
            if (field.virtual)
                continue;
            const level = getEffectiveFieldAuditLevel(field);
            if (level === 'off')
                continue;
            const oldValue = before === null ? undefined : beforeRec[key];
            const newValue = payloadRec[key];
            if (before === null && isEmptyValue(newValue))
                continue;
            if (before !== null && valuesEqual(oldValue, newValue, field))
                continue;
            if (level === 'names-only') {
                changes.push({
                    fieldName: key,
                    fieldLabel: field.label,
                    level: 'names-only',
                    oldValue: undefined,
                    newValue: undefined,
                });
            }
            else {
                changes.push({
                    fieldName: key,
                    fieldLabel: field.label,
                    level: 'full',
                    oldValue: before === null ? undefined : oldValue,
                    newValue,
                });
            }
            continue;
        }
        // No descriptor — closed failure: names-only, no values.
        if (before === null && isEmptyValue(payloadRec[key]))
            continue;
        if (before !== null && valuesEqual(beforeRec[key], payloadRec[key]))
            continue;
        changes.push({
            fieldName: key,
            fieldLabel: key,
            level: 'names-only',
            oldValue: undefined,
            newValue: undefined,
        });
    }
    return changes;
}
/**
 * Filtered document snapshot for the field-audit log.
 * Drop technical / `'off'` fields; omit `'names-only'` (names without values are
 * useless in a value snapshot). Missing descriptor ⇒ null.
 *
 * Used on delete (state before) and on child create/update (state after) so the
 * history UI can identify the row without re-reading it.
 */
export function buildAuditSnapshot(doc, entity) {
    const descriptor = getDescriptorForRule(entity, 'field-audit');
    if (!descriptor)
        return null;
    const out = {};
    for (const field of descriptor.fields) {
        if (TECHNICAL_FIELDS.has(field.name))
            continue;
        if (field.virtual)
            continue;
        const level = getEffectiveFieldAuditLevel(field);
        if (level === 'off' || level === 'names-only')
            continue;
        if (!Object.prototype.hasOwnProperty.call(doc, field.name))
            continue;
        out[field.name] = doc[field.name];
    }
    return out;
}
//# sourceMappingURL=auditDiff.js.map